import { useState, useCallback } from 'react';
import { getLocationByZipCode } from '../services';
import { SepomexResponse, Option } from '../types';

/**
 * Hook personalizado para gestionar operaciones relacionadas con códigos postales
 * Proporciona funcionalidad para consultar información de ubicación por CP
 */
export const useSepomexService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [locationData, setLocationData] = useState<SepomexResponse | null>(null);
  const [settlements, setSettlements] = useState<Option[]>([]);

  /**
   * Obtiene información de ubicación basada en un código postal
   * @param zipCode - Código postal a consultar (5 dígitos)
   * @returns Objeto con la información de ubicación o undefined en caso de error
   */
  const fetchLocationByZipCode = useCallback(async (zipCode: string): Promise<SepomexResponse | undefined> => {
    // Validación básica del código postal
    if (!zipCode || zipCode.length !== 5 || !/^\d+$/.test(zipCode)) {
      setError('El código postal debe tener 5 dígitos numéricos');
      setLocationData(null);
      setSettlements([]);
      return undefined;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Llamar al servicio para obtener los datos
      const data = await getLocationByZipCode(zipCode);
      
      // Guardar los datos en el estado
      setLocationData(data);
      setSettlements(data.settlements || []);
      
      return data;
    } catch (err) {
      // Manejar errores
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al consultar el código postal';
      
      setError(errorMessage);
      setLocationData(null);
      setSettlements([]);
      
      console.error('Error en useSepomexService:', errorMessage);
      return undefined;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Limpia los datos de ubicación almacenados
   */
  const clearLocationData = useCallback(() => {
    setLocationData(null);
    setSettlements([]);
    setError(null);
  }, []);

  return {
    fetchLocationByZipCode,
    clearLocationData,
    loading,
    error,
    locationData,
    settlements,
  };
}; 